import clsx from "clsx"
import { FC, memo, useMemo } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useGame } from "@/providers/GameProvider"
import { IEffectAppliedLog, IStatChange } from "@/store/apis/gameplayAPI"
import { OutlinedText } from "@/ui/OutlinedText"
import styles from "./LogEffectApplied.module.scss"

interface ILogEffectAppliedProps extends IEffectAppliedLog {
  isCurrentTurn: boolean
}

const LogEffectAppliedComponent: FC<ILogEffectAppliedProps> = ({
  isCurrentTurn,
  ...props
}) => {
  const { isMe, playerName, opponentName } = useGame()
  const { t } = useTranslation()

  const getDiffValue = (
    data: IStatChange | undefined,
    isInvert: boolean = true
  ) => {
    if (!data) return ""

    const diff = data.after - data.before
    const diffAbs = Math.abs(diff)
    const isPositive = isInvert ? diff < 0 : diff > 0

    return (
      <span
        className={clsx({
          green: isPositive,
          red: !isPositive
        })}
      >
        {diff > 0 ? `+${diffAbs}` : `-${diffAbs}`} <span>[{data.after}]</span>
      </span>
    )
  }

  const getDiffText = (
    data: IStatChange | undefined,
    deltaName: string,
    isInvert: boolean = true
  ) => {
    if (!data) return ""

    return (
      <>
        {" "}
        {deltaName} {getDiffValue(data, isInvert)}
      </>
    )
  }

  const text = useMemo(() => {
    let progress = null
    let block = null
    let mirroring = null
    let speedBonus = null

    let luck = null
    let stamina = null
    let speed = null

    const isMePlayer = isMe(props.details.targetPlayerId)
    const isEffect = props.details.remainsActive

    let freezing = null

    let name = (
      <OutlinedText
        className={clsx({
          "green-light": isMePlayer,
          "red-light": !isMePlayer
        })}
      >
        {isMePlayer ? playerName : opponentName}
      </OutlinedText>
    )
    let rival = (
      <OutlinedText
        className={clsx({
          "green-light": !isMePlayer,
          "red-light": isMePlayer
        })}
      >
        {isMePlayer ? opponentName : playerName}
      </OutlinedText>
    )

    if (!isCurrentTurn) {
      name = <span />
      rival = <span />
    }

    switch (props.details.effectType) {
      case "discharge":
        return (
          <Trans
            i18nKey="logs.effectApplied.discharge"
            components={{
              name
            }}
          />
        )
      case "dig_through_block":
        progress = props.details.changes?.progress
        if (!progress) return ""
        return (
          <Trans
            i18nKey={"logs.effectApplied.dig_through_block"}
            components={{
              name,
              value: <>{getDiffValue(progress)}</>
            }}
          />
        )
      case "freezing":
        freezing = props.details.changes?.freezingMovements
        if (!freezing) return ""

        return (
          <Trans
            i18nKey={"logs.effectApplied.freezing"}
            components={{
              name,
              value: <>{getDiffValue(freezing)}</>
            }}
          />
        )
      case "steal_runes_state":
        luck = props.details.changes?.luck
        stamina = props.details.changes?.stamina
        speed = props.details.changes?.speed

        return (
          <Trans
            i18nKey={`logs.effectApplied.steal_runes_state`}
            components={{
              value: (
                <>
                  {getDiffText(speed, t("runes.speed"), false)}
                  {getDiffText(luck, t("runes.luck"), false)}
                  {getDiffText(stamina, t("runes.stamina"), false)}
                </>
              ),
              name,
              rival
            }}
          />
        )
      case "rune_state":
        luck = props.details.changes?.luck
        stamina = props.details.changes?.stamina
        speed = props.details.changes?.speed

        return (
          <Trans
            i18nKey={"logs.effectApplied.rune_state"}
            components={{
              value: (
                <>
                  {getDiffText(speed, t("runes.speed"), false)}
                  {getDiffText(luck, t("runes.luck"), false)}
                  {getDiffText(stamina, t("runes.stamina"), false)}
                </>
              ),
              name
            }}
          />
        )
      case "inc_dig_distance":
        block = props.details.changes?.block
        progress = props.details.changes?.progress

        return (
          <Trans
            i18nKey={"logs.effectApplied.inc_dig_distance"}
            components={{
              name,
              value: (
                <>
                  {getDiffText(block, t("stones"))}
                  {getDiffText(progress, t("distance"))}
                  {isEffect &&
                    ` (${t(`effect_types.${props.details.effectType}.title`).toLocaleLowerCase()})`}
                </>
              )
            }}
          />
        )
      case "inc_block_distance":
        progress = props.details.changes?.block
        if (!progress) return ""

        return (
          <Trans
            i18nKey={"logs.effectApplied.inc_block_distance"}
            components={{
              name,
              value: <>{getDiffValue(progress)}</>
            }}
          />
        )
      case "speed_bonus":
        speedBonus = props.details.changes?.speedBonus
        if (!speedBonus) return ""

        return (
          <Trans
            i18nKey={"logs.effectApplied.speed_bonus"}
            components={{
              name,
              value: <>{getDiffValue(speedBonus, false)}</>
            }}
          />
        )
      case "rune_miming_delta": {
        const deltaLuck = props.details.changes?.deltaLuck
        const deltaStamina = props.details.changes?.deltaStamina
        const deltaSpeed = props.details.changes?.deltaSpeed

        const isAllSame =
          deltaSpeed?.before === deltaLuck?.before &&
          deltaSpeed?.before === deltaStamina?.before

        const value = isAllSame ? (
          <>
            {getDiffText(deltaSpeed || { before: 1, after: 1 }, "", false)}{" "}
            {t("toAll")}
          </>
        ) : (
          <>
            {getDiffText(deltaSpeed, t("runes.speed"), false)}
            {getDiffText(deltaLuck, t("runes.luck"), false)}
            {getDiffText(deltaStamina, t("runes.stamina"), false)}
          </>
        )

        return (
          <Trans
            i18nKey={"logs.effectApplied.rune_miming_delta"}
            components={{
              value,
              name
            }}
          />
        )
      }
      case "skip_dig": {
        const skipDigging = props.details.changes?.skipDiggingMovements
        if (!skipDigging) return ""

        return (
          <Trans
            i18nKey={"logs.effectApplied.skip_dig"}
            components={{
              name,
              value: <>{getDiffValue(skipDigging)}</>
            }}
          />
        )
      }
      case "auto_dig":
        block = props.details.changes?.block
        progress = props.details.changes?.progress

        return (
          <Trans
            i18nKey={"logs.effectApplied.auto_dig"}
            components={{
              name,
              value: (
                <>
                  {getDiffText(block, t("stones"))}
                  {getDiffText(progress, t("distance"))}
                </>
              )
            }}
          />
        )
      case "steal_opponents_digging":
        block = props.details.changes?.block
        progress = props.details.changes?.progress
        mirroring = props.details.changes?.mirroring

        return (
          <Trans
            i18nKey={"logs.effectApplied.steal_opponents_digging"}
            components={{
              name,
              value: (
                <>
                  {getDiffText(mirroring, "")}
                  {getDiffText(block, t("stones"))}
                  {getDiffText(progress, t("distance"))}
                </>
              )
            }}
          />
        )
    }

    return <></>
  }, [props])

  return <span className={clsx(styles.LogEffectApplied)}>{text}</span>
}

export const LogEffectApplied = memo(LogEffectAppliedComponent)
